import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, ShowerHeadIcon, BathIcon, ChevronDownIcon } from 'lucide-react';
import { KohlerQuoteForm } from './KohlerQuoteForm';
interface KohlerLandingPageProps {
  initialProduct?: string;
  onClose: () => void;
}
export function KohlerLandingPage({
  initialProduct,
  onClose
}: KohlerLandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string>(initialProduct || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialProduct) {
      setSelectedProduct(initialProduct);
    }
  }, [initialProduct]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedProduct) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <KohlerQuoteForm initialZip={zipCode} productSlug={selectedProduct} onClose={onClose} data-id="element-1904" />;
  }
  const products = [{
    id: 'showers',
    name: 'Showers',
    icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-1905" />,
    desc: 'Custom Walk-In Showers'
  }, {
    id: 'walk-in-baths',
    name: 'Walk-In Baths',
    icon: <BathIcon className="w-6 h-6" data-id="element-1906" />,
    desc: 'Safer, Comfortable Tubs'
  }];
  const productLabel = products.find(p => p.id === selectedProduct)?.name || '';
  const heroImages: Record<string, string> = {
    showers: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=1920&q=80',
    'walk-in-baths': 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=1920&q=80'
  };
  const heroImage = heroImages[selectedProduct] || 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1920&q=80';
  const heroHeadlines: Record<string, string> = {
    showers: 'Custom Walk-In Shower Remodels',
    'walk-in-baths': 'Safer, More Comfortable Walk-In Tubs'
  };
  const heroHeadline = selectedProduct ? heroHeadlines[selectedProduct] : 'Premium Showers & Walk-In Baths';
  // Dynamic branding based on selection
  const isShower = selectedProduct === 'showers';
  const brandColor = isShower ? '#C5D93D' : '#C5A55A'; // Lime green for showers, Gold for baths
  const brandDark = isShower ? '#9EAE31' : '#9E8448';
  const phone = isShower ? '(800) 704-9847' : '(800) 384-9034';
  const brandName = isShower ? 'Reveal by KOHLER' : 'KOHLER Walk-In Bath';
  return <div className="min-h-screen bg-[#FAFAFA] font-sans" data-id="element-1907">
      {/* Header */}
      <header className="bg-black py-4 px-6 md:px-12 flex justify-between items-center shadow-sm relative z-20" data-id="element-1908">
        <div className="flex flex-col" data-id="element-1909">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-[-4px]" data-id="element-1910">
            Partner
          </span>
          <div className="flex items-baseline gap-2" data-id="element-1911">
            <span className="text-2xl md:text-3xl font-black text-white tracking-tight" data-id="element-1912">
              KOHLER
            </span>
          </div>
          <span className="text-[10px] md:text-sm font-semibold text-gray-300" data-id="element-1913">
            Showers &bull; Walk-In Baths
          </span>
        </div>
        <div className="hidden md:flex items-center gap-4" data-id="element-1914">
          <span className="text-white font-medium" data-id="element-1915">
            Call for Your Free Quote:
          </span>
          <a href={`tel:${phone.replace(/\D/g, '')}`} className="text-xl font-bold" style={{
          color: brandColor
        }} data-id="element-1916">
            {phone}
          </a>
        </div>
      </header>

      {/* Offer Banner */}
      <section className="py-3 border-b border-gray-200" style={{
      backgroundColor: brandColor
    }} data-id="element-1917">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1918">
          <h2 className="text-sm md:text-base font-bold text-black tracking-tight" data-id="element-1919">
            Installed in as little as a day &bull; No Payments & No Interest for
            12 Months
          </h2>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-20 md:pb-32 overflow-hidden" data-id="element-1920">
        <div className="absolute inset-0 z-0" data-id="element-1921">
          <img src={heroImage} alt="Kohler bathroom" className="w-full h-full object-cover" data-id="element-1922" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-1923" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1924">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-1925">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1926">
              <span className="text-sm font-bold tracking-widest uppercase mb-4 block" style={{
              color: brandColor
            }} data-id="element-1927">
                Installed in as little as a day
              </span>
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-1928">
                {heroHeadline}
              </h1>
              <p className="text-lg md:text-xl font-medium text-gray-200 drop-shadow-md max-w-lg" data-id="element-1929">
                Connect with a KOHLER expert near you for a seamless bath
                remodel. Built on 150 years of expertise.
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-1930">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-1931">
                <h2 className="text-2xl font-bold text-black text-center mb-6" data-id="element-1932">
                  See If You Qualify for Local Discounts
                </h2>

                {/* Product dropdown (only if no initialProduct) */}
                {!initialProduct && <div className="mb-6 relative" ref={dropdownRef} data-id="element-1933">
                    <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-gray-50 ${selectedProduct ? 'border-black' : 'border-gray-200'} ${dropdownOpen ? 'border-black shadow-md' : 'hover:border-gray-300'}`} data-id="element-1934">
                      <div className="flex items-center gap-3" data-id="element-1935">
                        {selectedProduct ? <>
                            <span className="text-black" data-id="element-1936">
                              {products.find(p => p.id === selectedProduct)?.icon}
                            </span>
                            <span className="font-bold text-black text-lg" data-id="element-1937">
                              {products.find(p => p.id === selectedProduct)?.name}
                            </span>
                          </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-1938">
                            What are you looking for?
                          </span>}
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-black transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-1939" />
                    </button>

                    <AnimatePresence data-id="element-1940">
                      {dropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scaleY: 1
                  }} exit={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-gray-200 shadow-xl z-30 overflow-hidden origin-top" data-id="element-1941">
                          {products.map(prod => <button key={prod.id} onClick={() => {
                      setSelectedProduct(prod.id);
                      setDropdownOpen(false);
                    }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedProduct === prod.id ? 'bg-gray-50 text-black' : 'bg-white text-gray-700 hover:bg-gray-50 hover:text-black'} border-b border-gray-100 last:border-b-0`} data-id="element-1942">
                              <span className={`${selectedProduct === prod.id ? 'text-black' : 'text-gray-400'}`} data-id="element-1943">
                                {prod.icon}
                              </span>
                              <span className="font-bold text-lg" data-id="element-1944">
                                {prod.name}
                              </span>
                              {selectedProduct === prod.id && <CheckCircle2Icon className="w-5 h-5 text-black ml-auto" data-id="element-1945" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}

                {/* Show selected product badge when pre-selected */}
                {initialProduct && <div className="flex items-center justify-center gap-2 mb-6 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5" data-id="element-1946">
                    {products.find(p => p.id === initialProduct)?.icon}
                    <span className="font-bold text-black" data-id="element-1947">{productLabel}</span>
                  </div>}

                <p className="text-sm text-gray-600 mb-3 text-center" data-id="element-1948">
                  Find out how much a{' '}
                  {selectedProduct === 'showers' ? 'shower' : selectedProduct === 'walk-in-baths' ? 'walk-in bath' : 'shower or bath'}{' '}
                  remodel costs in your area.
                </p>

                {/* Zip Code Input */}
                <div className="relative mb-6" data-id="element-1949">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Zip Code" className="w-full h-14 px-6 text-xl font-medium bg-white border-2 border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-colors" maxLength={5} data-id="element-1950" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-1951">
                      <CheckCircle2Icon className="w-6 h-6" data-id="element-1952" />
                    </div>}
                </div>

                <button onClick={handleContinue} disabled={!isValidZip || !selectedProduct} className={`w-full h-14 rounded-lg font-bold text-lg text-black transition-all shadow-md ${isValidZip && selectedProduct ? 'hover:opacity-90 cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'}`} style={{
                backgroundColor: isValidZip && selectedProduct ? brandColor : undefined
              }} data-id="element-1953">
                  Get Started
                </button>

                {/* Trust Badges */}
                <div className="mt-6 flex items-center justify-center gap-4" data-id="element-1954">
                  <div className="flex items-center gap-2" data-id="element-1955">
                    <ShieldCheckIcon className="w-5 h-5 text-gray-400" data-id="element-1956" />
                    <span className="text-xs font-medium text-gray-500" data-id="element-1957">
                      Secure & Confidential
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 bg-white" data-id="element-1958">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1959">
          <div className="text-center mb-16" data-id="element-1960">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-1961">
              Trusted {productLabel || 'Products'} with Fast, Expert
              Installation
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-1962">
            {[{
            title: 'Professional Installation',
            desc: 'Completed in as little as a day by KOHLER-certified specialists.',
            img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=400&fit=crop'
          }, {
            title: 'Industry Leading Safety',
            desc: 'Including ADA-compliant features, easily-accessible controls, and safety grab bars.',
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
          }, {
            title: 'Lifetime Limited Warranty',
            desc: 'Backed by KOHLER for long-term confidence and protection.',
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2,
            duration: 0.5
          }} data-id="element-1963">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-1964">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-1965" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1966">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1967">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#FAFAFA]" data-id="element-1968">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1969">
          <div className="text-center mb-12" data-id="element-1970">
            <span className="text-sm font-bold tracking-widest uppercase text-gray-500 mb-2 block" data-id="element-1971">
              The Top-Rated Homeowners Trust
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-1972">
              Thousands of Five-Star Reviews
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1973">
            {[{
            name: 'Lyn',
            location: 'North Carolina',
            text: 'My mobility and balance are poor. My walk-in tub is very easy and safe to enter and exit. The jets massage my lower legs and lumbar area. Not only is this relaxing but I derive some pain relief which is much needed.'
          }, {
            name: 'Gerald',
            location: 'Pennsylvania',
            text: 'Ever since my wife broke her leg last year she hesitated to use the old tub. Now, she feels very safe using the new walk in with all of the features. She has no problem using the new tub.'
          }, {
            name: 'Bettie',
            location: 'Maryland',
            text: "I was most satisfied with KOHLER's installers. The installers were very cooperative, friendly, very professional, and they went above and beyond to get the job done."
          }].map((review, idx) => <motion.div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-1974">
                <div className="flex text-yellow-400 mb-4" data-id="element-1975">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-current" data-id="element-1976" />)}
                </div>
                <p className="text-gray-600 mb-6 text-sm leading-relaxed" data-id="element-1977">
                  {review.text}
                </p>
                <div data-id="element-1978">
                  <p className="font-bold text-gray-900" data-id="element-1979">
                    {review.name}, {review.location}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-16" data-id="element-1980">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1981">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-1982">
            <div className="flex flex-col gap-8" data-id="element-1983">
              <div className="flex flex-col" data-id="element-1984">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-[-4px]" data-id="element-1985">
                  Partner
                </span>
                <span className="text-3xl font-black text-white tracking-tight" data-id="element-1986">
                  KOHLER
                </span>
              </div>
              <p className="text-xs text-gray-400 font-medium" data-id="element-1987">
                &copy; {new Date().getFullYear()} findservicequotes.io. All
                Rights Reserved.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-1988">
              <div className="flex flex-col gap-4" data-id="element-1989">
                <a href="#" className="text-xs text-gray-300 hover:text-white font-medium" data-id="element-1990">
                  Home
                </a>
                <a href="#" className="text-xs text-gray-300 hover:text-white font-medium" data-id="element-1991">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-gray-300 hover:text-white font-medium" data-id="element-1992">
                  Terms Of Use
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-1993">
                <a href="#" className="text-xs text-gray-300 hover:text-white font-medium" data-id="element-1994">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-gray-300 hover:text-white font-medium" data-id="element-1995">
                  Do Not Sell My Personal Information
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8" data-id="element-1996">
            <p className="text-[10px] text-gray-500 leading-relaxed text-justify" data-id="element-1997">
              All estimates are free and no-obligation. Actual project costs may
              vary based on scope, materials, and local conditions. Licensed and
              insured. Contact us for full warranty details.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}